// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { AggregatorProvider } from '@shared/data-access/types/brokerage-types';
import { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';

export const usePlaidConnection = () => {
  const [token, setToken] = useState('' as string);
  const [publicToken, setPublicToken] = useState('' as string);
  const [getAdvisoryInstitutionsLink, { isLoading }] =
    advisoryApi.useGetAdvisoryInstitutionsLinkMutation();
  const [createAdvisoryInstitutions] =
    advisoryApi.useCreateAdvisoryInstitutionsMutation();

  const onSuccess = useCallback((token: string) => void setPublicToken(token), []);

  const { open, ready } = usePlaidLink({ token, onSuccess });

  const handleLinkAccount = useCallback(
    async (institutionId?: string) => {
      try {
        const { link_token } = await getAdvisoryInstitutionsLink({
          institution_id: institutionId ?? null,
        }).unwrap();

        setToken(link_token);
      } catch (error) {
        /* istanbul ignore next */
        console.error('Error creating link token', error);
      }
    },
    [getAdvisoryInstitutionsLink],
  );

  useEffect(() => {
    if (token && ready) open();
  }, [token, open, ready]);

  useEffect(() => {
    if (publicToken) {
      createAdvisoryInstitutions({
        public_token: publicToken,
        provider: AggregatorProvider.Plaid,
      });
    }
  }, [publicToken, createAdvisoryInstitutions]);

  return {
    handleLinkAccount,
    isLoading,
  };
};
